import React, { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FolderArchive, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { DateInput } from "@/components/DateInput";

const pad = (n) => String(n).padStart(2, "0");

const saveBlob = (r, fallback) => {
  const url = window.URL.createObjectURL(new Blob([r.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = r.headers["x-report-filename"] || fallback;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

const errMsg = async (e) => {
  try { return JSON.parse(await e.response.data.text()).detail; } catch { return null; }
};

// muvekkil=null → toplu (zip) mod
export const ClientPdfDialog = ({ muvekkil, selected, year, month, onClose }) => {
  const last = new Date(year, month, 0).getDate();
  const [start, setStart] = useState(`${year}-${pad(month)}-01`);
  const [end, setEnd] = useState(`${year}-${pad(month)}-${pad(last)}`);
  const [busy, setBusy] = useState("");
  const bulk = !muvekkil;

  const run = async (kind) => {
    if (!start || !end || start > end) { toast.error("Geçerli bir tarih aralığı seçin"); return; }
    setBusy(kind);
    try {
      const r = bulk
        ? await api.get(`/reports/muvekkil-bulk?${new URLSearchParams([["start", start], ["end", end], ...(selected || []).map((m) => ["muvekkil", m])])}`, { responseType: "blob" })
        : await api.get("/reports/muvekkil-pdf", { params: { muvekkil, start, end, format: kind }, responseType: "blob" });
      saveBlob(r, bulk ? "muvekkil_toplu.zip" : `muvekkil.${kind}`);
      toast.success(bulk ? "Toplu zip indirildi" : `Müvekkil ${kind === "xlsx" ? "Excel" : "PDF"} dosyası indirildi`);
      onClose();
    } catch (e) {
      toast.error((await errMsg(e)) || "Dosya oluşturulamadı");
    } finally { setBusy(""); }
  };

  const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-sm font-semibold disabled:opacity-50";
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="client-pdf-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{bulk ? "Toplu Müvekkil Çıktısı" : "Müvekkil Çıktısı"}</div>
            <div className="text-sm font-bold text-slate-900 truncate" title={muvekkil || ""} data-testid="client-pdf-name">{bulk ? (selected?.length ? `${selected.length} seçili müvekkil (zip)` : "Tüm müvekkiller (zip)") : muvekkil}</div>
          </div>
          <button onClick={onClose} data-testid="client-pdf-close" className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={14} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-2">
          <label className="text-[10px] font-bold text-slate-600">Başlangıç
            <DateInput value={start} onChange={(e) => setStart(e.target.value)} data-testid="client-pdf-start"
              className="mt-0.5 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </label>
          <label className="text-[10px] font-bold text-slate-600">Bitiş
            <DateInput value={end} onChange={(e) => setEnd(e.target.value)} data-testid="client-pdf-end"
              className="mt-0.5 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </label>
          <p className="col-span-2 text-[11px] text-slate-500">
            {bulk
              ? `${selected?.length ? "Seçilen ve aralıkta kaydı olan" : "Aralıkta kaydı olan"} her müvekkil için ayrı PDF ve Excel, ayrıca genel özet (PDF + Excel) tek zip dosyasında.`
              : "Dosyada özet toplamlar ve aralıktaki tüm tahsilat kayıtları yer alır."}
          </p>
        </div>
        <div className="p-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold">İptal</button>
          {bulk ? (
            <button onClick={() => run("zip")} disabled={!!busy} data-testid="client-bulk-download" className={`${btn} bg-emerald-600 hover:bg-emerald-700`}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <FolderArchive size={14} />} Zip İndir
            </button>
          ) : (
            <>
              <button onClick={() => run("xlsx")} disabled={!!busy} data-testid="client-xlsx-download" className={`${btn} bg-green-700 hover:bg-green-800`}>
                {busy === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel
              </button>
              <button onClick={() => run("pdf")} disabled={!!busy} data-testid="client-pdf-download" className={`${btn} bg-emerald-600 hover:bg-emerald-700`}>
                {busy === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
